import { useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate, useParams } from "@tanstack/react-router"

import type { HelpCenterTab } from "@productlane/zero"
import { isLooseGroup } from "@productlane/lib/src/help-center/loose-group"
import { queries } from "@productlane/zero/queries"

import type { GroupWithArticles } from "./types"
import { useActivePortalInstanceStore } from "@/stores/active-portal-instance-store"
import { useDocNavStore } from "@/stores/docs/docs-nav-store"
import { portalTabKey, useDocsTabStore } from "@/stores/docs/docs-tab-store"
import { toPublicTreeGroups } from "./public-tree-groups"

/** Groups whose tab is gone or on another portal count as the default tab. */
export function groupTabId(
	group: { tabId?: string | null },
	tabs: readonly Pick<HelpCenterTab, "id">[],
): string | null {
	const tabId = group.tabId ?? null
	return tabId && tabs.some((tab) => tab.id === tabId) ? tabId : null
}

export function useDocsTabs() {
	const [allTabs] = useQuery(queries.helpCenterTabs())
	const portalInstanceId = useActivePortalInstanceStore(
		(s) => s.activePortalInstanceId,
	)
	const params = useParams({ strict: false })
	const stored = useDocsTabStore(
		(s) => s.activeByPortal[portalTabKey(portalInstanceId)] ?? null,
	)
	const setActiveTab = useDocsTabStore((s) => s.setActiveTab)

	const tabs = useMemo(
		() =>
			allTabs.filter(
				(tab) => (tab.portalInstanceId ?? null) === portalInstanceId,
			),
		[allTabs, portalInstanceId],
	)
	const routeTabId = params.tabId ?? null
	const activeTabId =
		routeTabId && tabs.some((tab) => tab.id === routeTabId)
			? routeTabId
			: stored && tabs.some((tab) => tab.id === stored)
				? stored
				: null
	const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? null
	const apiTab = tabs.find((tab) => tab.kind === "API_REFERENCE") ?? null
	const hasDocsTabs = allTabs.some((tab) => tab.kind === "DOCS")

	return {
		tabs,
		allTabs,
		activeTabId,
		activeTab,
		apiTab,
		hasDocsTabs,
		portalInstanceId,
		setActiveTabId: (tabId: string | null) =>
			setActiveTab(portalInstanceId, tabId),
	}
}

/** The article the docs site opens for a tab: the first public row of its first group. */
export function firstTabArticleId(
	groups: readonly GroupWithArticles[],
	tabs: readonly Pick<HelpCenterTab, "id">[],
	tabId: string | null,
): string | null {
	return (
		toPublicTreeGroups(groups)
			.filter((group) => groupTabId(group, tabs) === tabId)
			.flatMap((group) => group.helpCenterArticles)[0]?.id ?? null
	)
}

/**
 * The top-level page the docs site opens a tab on in place of its own landing
 * (the docs home, an API reference's overview): the tab's first row, when that
 * row sits outside any group.
 */
export function landingReplacementArticleId(
	groups: readonly GroupWithArticles[],
	tabs: readonly Pick<HelpCenterTab, "id">[],
	tabId: string | null,
): string | null {
	const first = toPublicTreeGroups(groups).find(
		(group) =>
			groupTabId(group, tabs) === tabId && group.helpCenterArticles.length > 0,
	)
	return first && isLooseGroup(first)
		? (first.helpCenterArticles[0]?.id ?? null)
		: null
}

export const homeReplacementArticleId = (
	groups: readonly GroupWithArticles[],
	tabs: readonly Pick<HelpCenterTab, "id">[],
) => landingReplacementArticleId(groups, tabs, null)

/** The article a tab opens on, or null when it opens on its home or overview. */
export function tabLandingArticleId(
	groups: readonly GroupWithArticles[],
	tabs: readonly Pick<HelpCenterTab, "id">[],
	tab: Pick<HelpCenterTab, "id" | "kind"> | null,
): string | null {
	return tab?.kind === "DOCS"
		? firstTabArticleId(groups, tabs, tab.id)
		: landingReplacementArticleId(groups, tabs, tab?.id ?? null)
}

/**
 * Where a tab opens: the page readers land on. A landing article wins; without
 * one an API reference opens its overview and the default tab the docs home.
 */
export function docsTabLanding(
	tab: Pick<HelpCenterTab, "id" | "kind"> | null,
	landingArticleId: string | null,
): string {
	if (landingArticleId) return `/edit-docs/${landingArticleId}`
	if (tab?.kind === "API_REFERENCE") return `/edit-docs/api/${tab.id}`
	return "/edit-docs"
}

export function useSelectDocsTab() {
	const navigate = useNavigate()
	const { setActiveTabId } = useDocsTabs()
	return (
		tab: Pick<HelpCenterTab, "id" | "kind"> | null,
		landingArticleId: string | null = null,
	) => {
		const route = docsTabLanding(tab, landingArticleId)
		// The bare docs route reopens the last article; landing means the home.
		if (route === "/edit-docs")
			useDocNavStore.setState({ lastOpenedArticleId: "" })
		setActiveTabId(tab?.id ?? null)
		void navigate({ href: route })
	}
}

/** A remembered article from another tab stays closed when /edit-docs opens without an id. */
export function isRestoredFromOtherTab(args: {
	paramsId: string
	group: { tabId?: string | null } | undefined
	tabs: readonly Pick<HelpCenterTab, "id">[]
	activeTabId: string | null
}): boolean {
	return (
		!args.paramsId &&
		!!args.group &&
		groupTabId(args.group, args.tabs) !== args.activeTabId
	)
}

/**
 * Where the docs go after switching portals. A page of the old portal (an article,
 * a review, an API reference) gives way to the new portal's last tab.
 */
export function portalSwitchRoute(args: {
	portalInstanceId: string | null
	allTabs: readonly Pick<HelpCenterTab, "id" | "kind" | "portalInstanceId">[]
	storedTabId: string | null
	onPortalPage: boolean
}): string | null {
	const tab = args.allTabs.find(
		(t) =>
			t.id === args.storedTabId &&
			(t.portalInstanceId ?? null) === args.portalInstanceId,
	)
	if (tab?.kind === "API_REFERENCE") return `/edit-docs/api/${tab.id}`
	return args.onPortalPage ? "/edit-docs" : null
}
